import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, openPdf, printPdf } from "@/lib/api";
import { PageHeader, KV, StatusBadge, EmptyState } from "@/components/common/UI";
import { fmtDate, fmtDateTime, fmtTime, groupBySection } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Plus, FilePdf, Trash, FloppyDisk, Printer, PencilSimple } from "@phosphor-icons/react";
import MedicineRxList from "@/components/common/MedicineRxList";

export default function AdmissionDetailPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [adm, setAdm] = useState(null);
  const [patient, setPatient] = useState(null);
  const [progress, setProgress] = useState([]);
  const [bill, setBill] = useState(null);
  const [discharge, setDischarge] = useState(null);

  const load = useCallback(async () => {
    const a = (await api.get(`/admissions/${aid}`)).data;
    setAdm(a);
    const p = (await api.get(`/patients/${a.mrn}`)).data;
    setPatient(p);
    setProgress((await api.get(`/admissions/${aid}/progress`)).data || []);
    setBill((await api.get(`/admissions/${aid}/bill`)).data || null);
    setDischarge((await api.get(`/admissions/${aid}/discharge`)).data || null);
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  if (!adm || !patient) return <div className="text-sm text-[var(--hms-muted)]">Loading…</div>;

  return (
    <div data-testid="admission-detail-page">
      <Link to="/ipd" className="inline-flex items-center gap-1 text-xs text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] mb-3">
        <ArrowLeft size={12} weight="bold" /> Back to IPD
      </Link>

      <PageHeader
        eyebrow={adm.admission_no}
        title={patient.name}
        subtitle={`${adm.ward} / Bed ${adm.bed} · Dr. ${adm.doctor} · ${adm.admission_type || "Normal"} admission`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={adm.status} />
            <Button variant="outline" data-testid="print-admit" onClick={() => printPdf(`/pdf/admit-card/${adm.id}`).catch((e) => toast.error(e.message || "Print failed"))} className="rounded-sm border-[var(--hms-border-2)] bg-[var(--hms-paper)]">
              <Printer size={14} weight="bold" className="mr-1.5" /> Print Admit Card
            </Button>
            {bill && (
              <Button variant="outline" data-testid="print-bill" onClick={() => printPdf(`/pdf/bill/${adm.id}`).catch((e) => toast.error(e.message || "Print failed"))} className="rounded-sm border-[var(--hms-border-2)] bg-[var(--hms-paper)]">
                <Printer size={14} weight="bold" className="mr-1.5" /> Print Bill
              </Button>
            )}
            {discharge && (
              <Button variant="outline" data-testid="print-discharge" onClick={() => printPdf(`/pdf/discharge/${adm.id}`).catch((e) => toast.error(e.message || "Print failed"))} className="rounded-sm border-[var(--hms-border-2)] bg-[var(--hms-paper)]">
                <Printer size={14} weight="bold" className="mr-1.5" /> Print Discharge Card
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="hms-card p-4 lg:col-span-1">
          <div className="hms-section-title mb-3">Details</div>
          <div className="space-y-3">
            <KV label="MRN" value={<span className="font-mono">{patient.mrn}</span>} />
            <KV label="IPD No." value={<span className="font-mono">{adm.admission_no}</span>} />
            <KV label="Admit" value={fmtDateTime(adm.admit_date)} />
            <KV label="Discharge" value={fmtDateTime(adm.discharge_date)} />
            <KV label="Doctor" value={adm.doctor} />
            <KV label="Ward / Bed" value={`${adm.ward} / ${adm.bed}`} />
            <KV label="Diagnosis" value={adm.diagnosis} />
            <KV label="Allergies" value={patient.allergies} />
          </div>
        </div>

        <div className="lg:col-span-3">
          <Tabs defaultValue="progress" className="w-full">
            <TabsList className="bg-[var(--hms-paper)] rounded-sm border border-[var(--hms-border-2)]">
              <TabsTrigger value="progress" data-testid="tab-progress" className="rounded-sm data-[state=active]:bg-white">During Hospitalization</TabsTrigger>
              <TabsTrigger value="bill" data-testid="tab-bill" className="rounded-sm data-[state=active]:bg-white">Billing</TabsTrigger>
              <TabsTrigger value="discharge" data-testid="tab-discharge" className="rounded-sm data-[state=active]:bg-white">Discharge</TabsTrigger>
            </TabsList>

            <TabsContent value="progress" className="mt-3"><ProgressPanel aid={aid} list={progress} disabled={adm.status !== "admitted"} onSaved={load} /></TabsContent>
            <TabsContent value="bill" className="mt-3"><BillPanel aid={aid} adm={adm} bill={bill} onSaved={load} /></TabsContent>
            <TabsContent value="discharge" className="mt-3"><DischargePanel aid={aid} adm={adm} discharge={discharge} onDone={() => { load(); }} /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// Build current local datetime in "YYYY-MM-DDTHH:MM" for the datetime-local input default.
function _nowLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function _isoFromLocal(localStr) {
  // datetime-local "YYYY-MM-DDTHH:MM" → local Date → ISO with TZ
  if (!localStr) return null;
  const d = new Date(localStr);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
function _localFromIso(iso) {
  if (!iso) return _nowLocal();
  const d = new Date(iso);
  if (isNaN(d.getTime())) return _nowLocal();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const BLANK_NOTE = { bp: "", pulse: "", temp: "", spo2: "", rr: "", notes: "", doctor: "", medicines: [], created_at_local: "" };

function ProgressPanel({ aid, list, disabled, onSaved }) {
  const { user } = useAuth();
  const canDelete = !!user && (user.role === "admin" || user.role === "superuser");
  const [form, setForm] = useState(BLANK_NOTE);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    api.get("/doctors").then((r) => setDoctors(r.data || [])).catch(() => {});
  }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      bp: p.bp || "", pulse: p.pulse || "", temp: p.temp || "",
      spo2: p.spo2 || "", rr: p.rr || "", notes: p.notes || "",
      doctor: p.doctor || "", medicines: p.medicines || [],
      created_at_local: _localFromIso(p.created_at),
    });
    setTimeout(() => document.querySelector('[data-testid="progress-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const cancelEdit = () => { setEditing(null); setForm(BLANK_NOTE); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const medicines = (form.medicines || []).filter((m) => (m.name || "").trim());
      const created_at = _isoFromLocal(form.created_at_local);
      const payload = {
        bp: form.bp, pulse: form.pulse, temp: form.temp, spo2: form.spo2, rr: form.rr,
        notes: form.notes, doctor: form.doctor, medicines,
        ...(created_at ? { created_at } : {}),
      };
      if (editing) {
        await api.patch(`/admissions/${aid}/progress/${editing}`, payload);
        toast.success("Note updated");
      } else {
        await api.post(`/admissions/${aid}/progress`, payload);
        toast.success("Note saved");
      }
      setEditing(null);
      setForm(BLANK_NOTE);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete this progress note from ${fmtDateTime(p.created_at)}?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admissions/${aid}/progress/${p.id}`);
      toast.success("Note deleted");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {!disabled && (
        <form onSubmit={submit} className="hms-card p-4" data-testid="progress-form">
          <div className="flex items-center justify-between mb-3">
            <div className="hms-section-title">
              {editing ? "Edit progress note" : "New progress note"}
              {editing && <span className="ml-2 text-[10px] normal-case font-normal text-[var(--hms-muted)]">(saving will update this entry)</span>}
            </div>
            {editing && (
              <Button type="button" variant="ghost" onClick={cancelEdit} data-testid="vit-cancel" className="rounded-sm text-[var(--hms-text-2)]">
                Cancel
              </Button>
            )}
          </div>

          {/* Row 1: Date & Time + Doctor (dropdown) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Date &amp; Time</Label>
              <Input
                type="datetime-local"
                value={form.created_at_local}
                onChange={(e) => setForm({ ...form, created_at_local: e.target.value })}
                data-testid="vit-datetime"
                className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
              />
              <p className="text-[10px] text-[var(--hms-muted)] mt-1">Leave blank to stamp the current time. Backdated entries are allowed.</p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Doctor</Label>
              <Select value={form.doctor || ""} onValueChange={(v) => setForm({ ...form, doctor: v })}>
                <SelectTrigger data-testid="vit-doctor" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white">
                  <SelectValue placeholder={doctors.length ? "Select doctor…" : "Add doctors in Masters"} />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}{d.specialization ? ` · ${d.specialization}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Vitals — all in one line on md+ */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
            <V label="BP" value={form.bp} onChange={(v) => setForm({ ...form, bp: v })} testid="vit-bp" />
            <V label="Pulse" value={form.pulse} onChange={(v) => setForm({ ...form, pulse: v })} testid="vit-pulse" />
            <V label="Temp °F" value={form.temp} onChange={(v) => setForm({ ...form, temp: v })} testid="vit-temp" />
            <V label="SpO₂" value={form.spo2} onChange={(v) => setForm({ ...form, spo2: v })} testid="vit-spo2" />
            <V label="RR" value={form.rr} onChange={(v) => setForm({ ...form, rr: v })} testid="vit-rr" />
          </div>

          {/* Notes */}
          <div className="mt-3">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="vit-notes" />
          </div>

          {/* Medicines */}
          <div className="mt-3">
            <MedicineRxList
              value={form.medicines}
              onChange={(meds) => setForm({ ...form, medicines: meds })}
              title="Medicines (during hospitalization)"
              testidPrefix="progress-rx"
            />
          </div>

          {/* Save button — placed BELOW medicines, right-aligned */}
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={busy} data-testid="vit-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
              <FloppyDisk size={14} weight="bold" className="mr-1.5" />
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      )}

      {list.length === 0 ? <EmptyState title="No progress notes" hint="Vitals & notes will appear in chronological order." /> : (
        <div className="hms-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
              <tr>
                <th className="text-left px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">BP</th>
                <th className="text-left px-3 py-2">Pulse</th>
                <th className="text-left px-3 py-2">Temp</th>
                <th className="text-left px-3 py-2">SpO₂</th>
                <th className="text-left px-3 py-2">RR</th>
                <th className="text-left px-3 py-2">Notes</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className={`border-b border-[var(--hms-border)] ${editing === p.id ? "bg-[var(--hms-accent)]/5" : ""}`}>
                  <td className="px-3 py-2 hms-tabular">
                    {fmtDateTime(p.created_at)}
                    {p.updated_at && <div className="text-[9px] text-[var(--hms-muted)] uppercase tracking-wider mt-0.5">edited {fmtDateTime(p.updated_at)}</div>}
                  </td>
                  <td className="px-3 py-2 hms-tabular">{p.bp || "—"}</td>
                  <td className="px-3 py-2 hms-tabular">{p.pulse || "—"}</td>
                  <td className="px-3 py-2 hms-tabular">{p.temp || "—"}</td>
                  <td className="px-3 py-2 hms-tabular">{p.spo2 || "—"}</td>
                  <td className="px-3 py-2 hms-tabular">{p.rr || "—"}</td>
                  <td className="px-3 py-2 text-[var(--hms-text-2)]">
                    {p.notes || "—"}
                    {p.medicines && p.medicines.length > 0 && (
                      <div className="mt-1.5 text-[11px] border-t border-[var(--hms-border)] pt-1.5">
                        <span className="font-bold text-[var(--hms-text-2)] uppercase tracking-wider text-[9px] mr-1">Rx:</span>
                        {p.medicines.map((m, i) => (
                          <span key={i} className="text-[var(--hms-ink)]">
                            {i > 0 && <span className="text-[var(--hms-muted)]"> · </span>}
                            <span className="font-medium">{m.name}</span>
                            {m.schedule && <span className="font-mono text-[var(--hms-accent)] ml-1">{m.schedule}</span>}
                            {m.dose && <span className="text-[var(--hms-muted)] ml-1">({m.dose})</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--hms-muted)]">
                    {p.updated_by_name || p.created_by_name || "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {p.medicines && p.medicines.length > 0 && (
                      <Button size="sm" variant="ghost"
                        onClick={() => printPdf(`/pdf/progress-rx/${aid}/${p.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                        data-testid={`progress-print-rx-${p.id}`}
                        className="rounded-sm h-7 px-2 text-[var(--hms-sidebar)] hover:text-[var(--hms-ink-2)]"
                        title="Print prescription (Rx only)"
                      >
                        <Printer size={13} weight="bold" />
                      </Button>
                    )}
                    {!disabled && (
                      <Button size="sm" variant="ghost" onClick={() => startEdit(p)} data-testid={`progress-edit-${p.id}`} className="rounded-sm h-7 px-2 text-[var(--hms-text-2)] hover:text-[var(--hms-ink)]" title="Edit note">
                        <PencilSimple size={13} weight="bold" />
                      </Button>
                    )}
                    {!disabled && canDelete && (
                      <Button size="sm" variant="ghost" onClick={() => del(p)} data-testid={`progress-del-${p.id}`} className="rounded-sm h-7 px-2 text-[var(--hms-accent)]" title="Delete note (admin)">
                        <Trash size={13} weight="bold" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function V({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}

function BillPanel({ aid, adm, bill, onSaved }) {
  const { user } = useAuth();
  const canDeleteReceipt = user && (user.role === "admin" || user.role === "superuser");

  // Compute a default first-row "Bed Charges" line seeded from the admission's
  // bed daily rate × days admitted. User can freely edit qty/rate afterwards.
  const buildDefaultRows = () => {
    const rate = Number(adm?.bed_daily_rate || 0);
    if (!rate) return [{ description: "", category: "Room", qty: 1, rate: 0, amount: 0 }];
    const admitMs = adm?.admit_date ? new Date(adm.admit_date).getTime() : null;
    const endMs = adm?.discharge_date ? new Date(adm.discharge_date).getTime() : Date.now();
    let days = 1;
    if (admitMs && endMs && endMs >= admitMs) {
      days = Math.max(1, Math.ceil((endMs - admitMs) / 86400000));
    }
    return [{
      description: `Bed Charges (${adm?.ward || ""} - ${adm?.bed || ""})`,
      category: "Room",
      qty: days,
      rate,
      amount: Number((days * rate).toFixed(2)),
    }];
  };

  const [items, setItems] = useState(bill?.items?.length ? bill.items : buildDefaultRows());
  const [waiveOff, setWaiveOff] = useState(Number(bill?.waive_off || 0));
  const [waiveOffReason, setWaiveOffReason] = useState(bill?.waive_off_reason || "");
  const [charges, setCharges] = useState([]);
  useEffect(() => {
    setItems(bill?.items?.length ? bill.items : buildDefaultRows());
    setWaiveOff(Number(bill?.waive_off || 0));
    setWaiveOffReason(bill?.waive_off_reason || "");
  }, [bill, adm?.bed_daily_rate, adm?.admit_date, adm?.discharge_date]);
  useEffect(() => { api.get("/charges").then((r) => setCharges(r.data || [])).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", mode: "Cash", reference: "", date: "" });

  const updateRow = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    if (k === "qty" || k === "rate") {
      next[i].amount = Number(next[i].qty || 0) * Number(next[i].rate || 0);
    }
    setItems(next);
  };
  const pickCharge = (i, chargeId) => {
    const c = charges.find((x) => x.id === chargeId);
    if (!c) return;
    const next = [...items];
    next[i] = {
      ...next[i],
      description: c.name,
      category: c.category || "Service",
      rate: Number(c.rate || 0),
      qty: Number(next[i].qty || 1),
      amount: Number(c.rate || 0) * Number(next[i].qty || 1),
    };
    setItems(next);
  };
  const addRow = () => setItems([...items, { description: "", category: "Service", qty: 1, rate: 0, amount: 0 }]);
  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const total = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.rate || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admissions/${aid}/bill`, {
        items: items.map((it) => ({ ...it, qty: Number(it.qty || 0), rate: Number(it.rate || 0), amount: Number(it.qty || 0) * Number(it.rate || 0) })),
        waive_off: Number(waiveOff || 0),
        waive_off_reason: waiveOffReason || "",
      });
      toast.success("Bill saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addPayment = async () => {
    if (!pay.amount) return toast.error("Enter amount");
    try {
      // datetime-local gives "YYYY-MM-DDTHH:MM" in the user's local TZ. Append
      // seconds so backend ISO parser is happy; omit the field if blank so
      // backend stamps now() — most common case.
      const payload = {
        amount: Number(pay.amount),
        mode: pay.mode,
        reference: pay.reference,
      };
      if (pay.date) payload.date = pay.date.length === 16 ? `${pay.date}:00` : pay.date;
      await api.post(`/admissions/${aid}/bill/payments`, payload);
      toast.success("Payment recorded");
      setPay({ amount: "", mode: "Cash", reference: "", date: "" });
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deletePayment = async (i) => {
    if (!window.confirm(`Delete receipt ${bill.bill_no}-P${String(i + 1).padStart(2, "0")}?\nThis will decrement the paid total.`)) return;
    try {
      await api.delete(`/admissions/${aid}/bill/payments/${i}`);
      toast.success("Receipt deleted");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const paid = Number(bill?.paid || 0);
  const grossTotal = bill ? Number(bill.total || 0) : total;
  const waiveOffApplied = Number(bill?.waive_off || waiveOff || 0);
  const netPayable = Math.max(0, grossTotal - waiveOffApplied);
  const balance = netPayable - paid;

  return (
    <div className="space-y-4">
      <div className="hms-card p-4" data-testid="bill-editor">
        <div className="flex items-center justify-between mb-3">
          <div className="hms-section-title">Itemized charges {bill && <span className="font-mono text-[10px] ml-2">{bill.bill_no}</span>}</div>
          <Button onClick={save} disabled={busy} data-testid="bill-save" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <FloppyDisk size={14} weight="bold" className="mr-1.5" /> {busy ? "Saving…" : (bill ? "Update bill" : "Generate bill")}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
              <tr>
                <th className="text-left py-2 px-2 w-1/5">Charge (master)</th>
                <th className="text-left py-2 px-2 w-2/5">Description</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-right py-2 px-2">Qty</th>
                <th className="text-right py-2 px-2">Rate</th>
                <th className="text-right py-2 px-2">Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-[var(--hms-border)]" data-testid={`bill-row-${i}`}>
                  <td className="py-1 px-2">
                    <Select value="" onValueChange={(v) => pickCharge(i, v)}>
                      <SelectTrigger className="rounded-sm border-[var(--hms-border)] bg-white h-8" data-testid={`bill-charge-${i}`}>
                        <SelectValue placeholder={charges.length ? "Pick…" : "No charges"} />
                      </SelectTrigger>
                      <SelectContent>
                        {charges.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="font-mono text-[10px]">{c.code}</span> {c.name} <span className="text-[var(--hms-muted)]">· ₹{Number(c.rate).toFixed(0)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-1 px-2"><Input value={it.description} onChange={(e) => updateRow(i, "description", e.target.value)} placeholder="e.g. Room rent (Day 1)" className="rounded-sm border-[var(--hms-border)] bg-white h-8" data-testid={`bill-desc-${i}`} /></td>
                  <td className="py-1 px-2">
                    <Select value={it.category} onValueChange={(v) => updateRow(i, "category", v)}>
                      <SelectTrigger className="rounded-sm border-[var(--hms-border)] bg-white h-8 w-32" data-testid={`bill-cat-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Room", "Doctor", "Nursing", "Pharmacy", "Lab", "Procedure", "Service", "Other"].map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-1 px-2"><Input type="number" value={it.qty} onChange={(e) => updateRow(i, "qty", e.target.value)} className="rounded-sm border-[var(--hms-border)] bg-white h-8 text-right w-20 hms-tabular" data-testid={`bill-qty-${i}`} /></td>
                  <td className="py-1 px-2"><Input type="number" value={it.rate} onChange={(e) => updateRow(i, "rate", e.target.value)} className="rounded-sm border-[var(--hms-border)] bg-white h-8 text-right w-24 hms-tabular" data-testid={`bill-rate-${i}`} /></td>
                  <td className="py-1 px-2 text-right hms-tabular font-medium">₹{(Number(it.qty || 0) * Number(it.rate || 0)).toFixed(2)}</td>
                  <td className="py-1 px-2"><Button size="sm" variant="ghost" onClick={() => removeRow(i)} className="text-[var(--hms-accent)] rounded-sm" data-testid={`bill-rm-${i}`}><Trash size={14} weight="bold" /></Button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="py-2 px-2"><Button variant="outline" size="sm" onClick={addRow} className="rounded-sm border-[var(--hms-border-2)]" data-testid="bill-add-row"><Plus size={12} weight="bold" className="mr-1" /> Add line</Button></td>
                <td className="py-2 px-2 text-right">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--hms-muted)]">Total</div>
                  <div className="font-heading text-lg font-extrabold hms-tabular">₹{total.toFixed(2)}</div>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Waived off + summary cards */}
      <div className="hms-card p-4" data-testid="waive-off-panel">
        <div className="hms-section-title mb-3">Waived off / Discount</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Waived Off (₹)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={waiveOff}
              onChange={(e) => setWaiveOff(e.target.value)}
              data-testid="waive-off-amount"
              className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white font-mono"
              placeholder="0.00"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">
              Reason <span className="text-[var(--hms-muted)] normal-case font-normal">· optional</span>
            </Label>
            <Input
              value={waiveOffReason}
              onChange={(e) => setWaiveOffReason(e.target.value)}
              placeholder="Senior citizen discount / Insurance gap / Hardship case"
              data-testid="waive-off-reason"
              className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
            />
          </div>
        </div>
        <p className="text-[10px] text-[var(--hms-muted)] mt-2">
          Click <b>Save bill</b> above to persist the waived-off amount. It reduces the patient&apos;s net payable and balance due.
        </p>
      </div>

      {bill && (
        <div className={`grid grid-cols-1 ${waiveOffApplied > 0 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4`}>
          <div className="hms-card p-4">
            <div className="hms-section-title">{waiveOffApplied > 0 ? "Subtotal" : "Total"}</div>
            <div className="hms-stat text-2xl">₹{Number(bill.total).toFixed(2)}</div>
          </div>
          <div className="hms-card p-4">
            <div className="hms-section-title">Paid</div>
            <div className="hms-stat text-2xl text-[var(--hms-success)]">₹{paid.toFixed(2)}</div>
          </div>
          {waiveOffApplied > 0 && (
            <div className="hms-card p-4 border-[var(--hms-accent)]/40">
              <div className="hms-section-title">Waived Off</div>
              <div className="hms-stat text-2xl text-[var(--hms-accent)]">−₹{waiveOffApplied.toFixed(2)}</div>
              {bill.waive_off_reason && <div className="text-[10px] text-[var(--hms-muted)] mt-1 truncate" title={bill.waive_off_reason}>{bill.waive_off_reason}</div>}
            </div>
          )}
          <div className="hms-card p-4 border-[var(--hms-accent)]/40">
            <div className="hms-section-title">Balance</div>
            <div className="hms-stat text-2xl text-[var(--hms-accent)]">₹{balance.toFixed(2)}</div>
          </div>
        </div>
      )}

      <div className="hms-card p-4" data-testid="payments-panel">
        <div className="flex items-center gap-2 mb-3">
          <div className="hms-section-title">{bill && bill.items?.length ? "Record payment" : "Record advance payment"}</div>
          {(!bill || !bill.items?.length) && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#b96b27] bg-[#F4A261]/15 border border-[#F4A261]/30 px-2 py-0.5 rounded-sm">
              Advance · before bill
            </span>
          )}
        </div>
        {(!bill || !bill.items?.length) && (
          <p className="text-xs text-[var(--hms-text-2)] mb-3 -mt-1">
            No bill items yet. You can still collect a deposit / advance now — it will be auto-credited when the bill is generated.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Date &amp; Time <span className="text-[var(--hms-muted)] normal-case font-normal">· optional</span></Label>
            <Input
              type="datetime-local"
              value={pay.date}
              onChange={(e) => setPay({ ...pay, date: e.target.value })}
              className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
              data-testid="pay-date"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Amount</Label>
            <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="pay-amount" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Mode</Label>
              <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                <SelectTrigger className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Cash", "Card", "UPI", "Cheque", "Insurance"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Reference (txn id / cheque no.)</Label>
              <Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="pay-ref" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button onClick={addPayment} className="w-full rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]" data-testid="pay-submit">
                Record payment
              </Button>
            </div>
          </div>

          {bill?.payments?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <div className="hms-section-title mb-2">Payment receipts</div>
              <table className="min-w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
                  <tr>
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Date &amp; Time</th>
                    <th className="text-left py-2">Mode</th>
                    <th className="text-left py-2">Reference</th>
                    <th className="text-left py-2">By</th>
                    <th className="text-right py-2">Amount</th>
                    <th className="text-right py-2">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.payments.map((p, i) => (
                    <tr key={i} className="border-b border-[var(--hms-border)]" data-testid={`pay-row-${i}`}>
                      <td className="py-2 font-mono text-[10px] text-[var(--hms-muted)]">{`${bill.bill_no}-P${String(i + 1).padStart(2, "0")}`}</td>
                      <td className="py-2 hms-tabular">{fmtDateTime(p.date)}</td>
                      <td className="py-2">{p.mode}</td>
                      <td className="py-2">{p.reference || "—"}</td>
                      <td className="py-2 text-[var(--hms-muted)]">
                        {p.by || "—"}
                        {p.is_advance && (
                          <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-[#b96b27] bg-[#F4A261]/15 border border-[#F4A261]/30 px-1.5 py-0.5 rounded-sm">Advance</span>
                        )}
                      </td>
                      <td className="py-2 text-right hms-tabular font-medium">₹{Number(p.amount).toFixed(2)}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => printPdf(`/pdf/payment/${aid}/${i}`).catch((e) => toast.error(e.message || "Print failed"))} className="rounded-sm" data-testid={`pay-print-${i}`}>
                          <Printer size={13} weight="bold" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openPdf(`/pdf/payment/${aid}/${i}`, `receipt_${bill.bill_no}_P${i + 1}.pdf`).catch((e) => toast.error(e.message || "PDF failed"))} className="rounded-sm" data-testid={`pay-pdf-${i}`}>
                          <FilePdf size={13} weight="bold" />
                        </Button>
                        {canDeleteReceipt && (
                          <Button size="sm" variant="ghost" onClick={() => deletePayment(i)} className="rounded-sm text-[var(--hms-accent)]" data-testid={`pay-del-${i}`} title="Delete receipt (admin only)">
                            <Trash size={13} weight="bold" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
    </div>
  );
}

function DischargePanel({ aid, adm, discharge, onDone }) {
  const [form, setForm] = useState(discharge || {
    discharge_type: "Normal",
    final_diagnosis: adm.diagnosis || "", history: "", investigations: "", treatment_given: "",
    condition_at_discharge: "Stable", additional_notes: "", medicines: [],
  });
  useEffect(() => {
    if (discharge) {
      // Migrate legacy upper-case values to the new title-case enum so the
      // dropdown still binds correctly.
      const TMAP = { NORMAL: "Normal", DAMA: "DAMA", DEATH: "Death" };
      const dt = TMAP[(discharge.discharge_type || "").toUpperCase()] || discharge.discharge_type || "Normal";
      // Merge any legacy follow_up text into additional_notes so it remains
      // visible in the renamed combined field.
      const legacyFollow = (discharge.follow_up || "").trim();
      const existingNotes = (discharge.additional_notes || discharge.medications || "").trim();
      const merged = [existingNotes, legacyFollow && `Follow-up: ${legacyFollow}`].filter(Boolean).join("\n");
      setForm({
        ...discharge,
        discharge_type: dt,
        additional_notes: merged,
        medicines: discharge.medicines || [],
      });
    }
  }, [discharge]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        follow_up: "", // merged into additional_notes
        medicines: (form.medicines || []).filter((m) => (m.name || "").trim()),
      };
      await api.post(`/admissions/${aid}/discharge`, payload);
      toast.success("Discharge summary saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const txt = (k, label, rows = 3) => (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}</Label>
      <Textarea
        rows={rows}
        value={form[k] || ""}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
        data-testid={`disc-${k}`}
      />
    </div>
  );

  const clearAdditional = () => setForm({ ...form, additional_notes: "" });

  return (
    <form onSubmit={submit} className="hms-card p-4 space-y-3" data-testid="discharge-form">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="hms-section-title">Discharge summary {discharge && <span className="text-[10px] ml-2 text-[var(--hms-muted)]">(editable until next save)</span>}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Discharge Type:</Label>
            <Select value={form.discharge_type || "Normal"} onValueChange={(v) => setForm({ ...form, discharge_type: v })}>
              <SelectTrigger className="h-8 w-[140px] rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="disc-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Normal" data-testid="disc-type-normal">Normal</SelectItem>
                <SelectItem value="DAMA" data-testid="disc-type-dama">DAMA</SelectItem>
                <SelectItem value="Death" data-testid="disc-type-death">Death</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} data-testid="discharge-submit" className="rounded-sm bg-[var(--hms-accent)] hover:bg-[var(--hms-accent)]/90">
            {busy ? "Saving…" : adm.status === "discharged" ? "Update summary" : "Save & discharge patient"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {txt("history", "Symptoms / Patient History", 4)}
        {txt("investigations", "Examination & Investigations", 4)}
        <div className="md:col-span-2">{txt("final_diagnosis", "Final Diagnosis", 2)}</div>
        {txt("treatment_given", "Treatment Given", 4)}
        {txt("condition_at_discharge", "Condition at Discharge", 4)}

        <div className="md:col-span-2">
          <MedicineRxList
            value={form.medicines}
            onChange={(meds) => setForm({ ...form, medicines: meds })}
            title="Discharge Medicines (Rx — printed on discharge card)"
            testidPrefix="discharge-rx"
          />
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Additional Notes / Follow Up</Label>
            {(form.additional_notes || "").trim() && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearAdditional}
                data-testid="disc-additional-clear"
                className="h-7 rounded-sm border-[var(--hms-border-2)] bg-[var(--hms-paper)] text-[11px]"
              >
                <Trash size={12} weight="bold" className="mr-1" /> Delete
              </Button>
            )}
          </div>
          <Textarea
            rows={4}
            value={form.additional_notes || ""}
            onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
            placeholder="Add any additional note, advice, or follow-up plan…"
            className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
            data-testid="disc-additional_notes"
          />
        </div>
      </div>
    </form>
  );
}
